import React from 'react';

const DEFAULT_GOOD: { bg: string; color: string } = {
  bg: '#DCFCE7',
  color: '#15803D',
};

const DEFAULT_INDIFFERENT: { bg: string; color: string } = {
  bg: '#FEF9C3',
  color: '#854D0E',
};

const DEFAULT_BAD: { bg: string; color: string } = {
  bg: '#FEE2E2',
  color: '#B91C1C',
};

export const APPROVAL_STYLES: Record<string, { bg: string; color: string }> = {
  Approved: DEFAULT_GOOD,
  Sent: DEFAULT_INDIFFERENT,
  Rejected: DEFAULT_BAD,
};

export const LEVEL_OF_EFFORT_STYLES: Record<
  string,
  { bg: string; color: string }
> = {
  Low: DEFAULT_GOOD,
  Medium: DEFAULT_INDIFFERENT,
  High: DEFAULT_BAD,
};

export const SCOPE_OF_RETURN_STYLES: Record<
  string,
  { bg: string; color: string }
> = {
  Low: { bg: '#F3F4F6', color: '#374151' },
  Medium: { bg: '#DBEAFE', color: '#1D4ED8' },
  High: { bg: '#F3E8FF', color: '#7E22CE' },
};

export const TICKET_PRIORITY_STYLES: Record<
  string,
  { bg: string; color: string }
> = {
  P3: DEFAULT_GOOD,
  P2: DEFAULT_INDIFFERENT,
  P1: DEFAULT_BAD,
};

export const TICKET_IMPACT_STYLES: Record<
  string,
  { bg: string; color: string }
> = {
  Low: DEFAULT_GOOD,
  Medium: DEFAULT_INDIFFERENT,
  High: DEFAULT_BAD,
};

export const TICKET_URGENCY_STYLES: Record<
  string,
  { bg: string; color: string }
> = {
  Low: DEFAULT_GOOD,
  Medium: DEFAULT_INDIFFERENT,
  High: DEFAULT_BAD,
};

export const TICKET_STATUS_STYLES: Record<
  string,
  { bg: string; color: string }
> = {
  OPEN: { bg: '#f0f5ff', color: '#2f54eb' },
  RESOLVED: { bg: '#FEF9C3', color: '#854D0E' },
  CLOSED: DEFAULT_GOOD,
};

export const TICKET_SUB_STATUS_STYLES: Record<
  string,
  { bg: string; color: string }
> = {
  COMPLETE: DEFAULT_GOOD,
  CANCELLED: { bg: '#fff2e8', color: '#d4380d' },
  REJECTED: { bg: '#fff2f0', color: '#cf1322' },
};

// Opportunity status styles — hex equivalents of the Ant Design Tag preset
// colors used by OptimizeStatus. Values match Ant Design's light-mode palette.
export const OPPORTUNITY_STATUS_STYLES: Record<
  string,
  { bg: string; color: string }
> = {
  Draft: { bg: '#fafafa', color: '#595959' },
  'Pending Review': { bg: '#fffbe6', color: '#d48806' },
  Approved: { bg: '#f0f5ff', color: '#2f54eb' },
  'In Progress': { bg: '#e6f4ff', color: '#0958d9' },
  Verifying: { bg: '#e6fffb', color: '#08979c' },
  'Savings Booked': { bg: '#f6ffed', color: '#389e0d' },
  Rejected: { bg: '#fff2f0', color: '#cf1322' },
  Deferred: { bg: '#fff7e6', color: '#d46b08' },
  Cancelled: { bg: '#fff2e8', color: '#d4380d' },
};

export const OVERLAY_NETWORK_STATUS: Record<
  string,
  { bg: string; color: string }
> = {
  'Network Access Enabled': { bg: '#f6ffed', color: '#389e0d' },
  'Network Access Disabled': { bg: '#fff2f0', color: '#cf1322' },
};

const badge = (
  value: string,
  styles: Record<string, { bg: string; color: string }>,
  label: string = value,
) => {
  const sty = styles[value] ?? { bg: '#F3F4F6', color: '#374151' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 700,
        background: sty.bg,
        color: sty.color,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
};

type Props = {
  value: string;
  config: Record<string, { bg: string; color: string }>;
  // Text to show when it differs from the value the style is keyed on.
  label?: string;
};

const TagDisplay = ({ value, config, label }: Props) => {
  if (!value) {
    return null;
  }

  return badge(value, config, label);
};

export default TagDisplay;
